import { ICONS } from "../../constants/icons"

/**
 * City Hub tab registry.
 * `path` is the nested route segment under `/city-hub/:cityId/`.
 * Keep `hidden` entries so they can be re-enabled without restoring code.
 */
export const CITY_HUB_TABS = [
  {
    key: "calls-for-artists",
    path: "calls-for-artists",
    label: "Inboxes & Calls for Artists",
    pageTitle: "Inboxes & Calls for Artists",
    pageSubtitle:
      "Looking for a drummer or a supporting band? Technical feedback on cameras or recording software? Ask your city by creating an Inbox!",
    documentTitle: "Inboxes & Calls for Artists | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: "Create Call",
    ctaIcon: "plus",
    pagination: "infinite",
    icon: ICONS.callFormalSearchStatus,
  },
  {
    key: "city-inbox",
    path: "city-inbox",
    label: "City Inbox",
    pageTitle: "City Inbox",
    pageSubtitle: "Stay updated on conversations and topics happening in your city.",
    documentTitle: "City Inbox | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: null,
    pagination: "infinite",
    icon: ICONS.messageText,
    hidden: true,
  },
  {
    key: "shadow-working-events",
    path: "shadow-working-events",
    label: "Shadow Working Events",
    pageTitle: "Shadow Working Events",
    pageSubtitle:
      "Join in on a public Shadow Working event hosted by people in your city.",
    documentTitle: "Shadow Working Events | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: "Start Session",
    ctaIcon: "cityHubTimer",
    pagination: "infinite",
    icon: ICONS.calendar,
    // legacy key used by older detail deep-links / state
    legacyKeys: ["events"],
  },
  {
    key: "local-reviews",
    path: "local-reviews",
    label: "Local Reviews",
    pageTitle: "Local Reviews",
    pageSubtitle:
      "Read honest reviews and ratings from the creative community near you.",
    documentTitle: "Local Reviews | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: "Write a Review",
    ctaIcon: "plus",
    pagination: "infinite",
    icon: ICONS.cityHubStar,
    legacyKeys: ["reviews"],
  },
  {
    key: "photography",
    path: "photography",
    label: "Photography",
    pageTitle: "Photography",
    pageSubtitle:
      "Explore and share photography work from artists in your city.",
    documentTitle: "Photography | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: "Contribute",
    ctaIcon: "plus",
    pagination: "infinite",
    icon: ICONS.cityHubCamera,
  },
  {
    key: "blogs",
    path: "blogs",
    label: "Blogs",
    pageTitle: "Blogs",
    pageSubtitle: "Stories and updates from your city.",
    documentTitle: "Blogs | City Hub",
    searchPlaceholder: "Search by Title...",
    showFilter: true,
    ctaLabel: null,
    pagination: "infinite",
    icon: ICONS.navCallsForArtist,
    hidden: true,
  },
  {
    key: "creators",
    path: "creators",
    label: "Creators",
    pageTitle: "Creators",
    pageSubtitle: "Discover the talented creators in your area.",
    documentTitle: "Creators | City Hub",
    searchPlaceholder: "Search by name...",
    showFilter: true,
    ctaLabel: null,
    pagination: "paged",
    icon: ICONS.cityHubCreator,
  },
]

export const VISIBLE_CITY_HUB_TABS = CITY_HUB_TABS.filter(tab => !tab.hidden)

export const CITY_HUB_FILTER_OPTIONS = [
  { value: "music", label: "Music" },
  { value: "film", label: "Film" },
  { value: "visual-arts", label: "Visual Arts" },
  { value: "performing-arts", label: "Performing Arts" },
  { value: "literary", label: "Literary" },
]

export const CITY_HUB_INFINITE_PAGE_SIZE = 6
/** Public city listings only — keep member City Hub at CITY_HUB_INFINITE_PAGE_SIZE. */
export const PUBLIC_CITY_INFINITE_PAGE_SIZE = 10
export const CITY_HUB_CREATORS_PAGE_SIZE = 12

/**
 * Normalize list + pagination from City Hub / public list responses.
 * Prefers next_page_url / last_page; falls back to full-page length.
 */
export const unwrapCityHubPagedResult = (
  payload,
  { pageToLoad = 1, pageSize = CITY_HUB_INFINITE_PAGE_SIZE } = {},
) => {
  const result = payload?.data?.result ?? payload?.result ?? {}
  const list = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []
  const meta =
    result && typeof result === "object" && !Array.isArray(result)
      ? result.meta || {}
      : {}

  const currentPage =
    Number(
      (!Array.isArray(result) && result.current_page) ??
        meta.current_page ??
        pageToLoad,
    ) || pageToLoad

  const lastPageRaw = !Array.isArray(result)
    ? result.last_page ?? meta.last_page
    : null
  const hasExplicitLastPage =
    lastPageRaw != null &&
    lastPageRaw !== "" &&
    !Number.isNaN(Number(lastPageRaw))
  const lastPage = hasExplicitLastPage ? Number(lastPageRaw) || 1 : null

  const nextPageUrl = !Array.isArray(result)
    ? result.next_page_url ?? meta.next_page_url ?? null
    : null

  let hasMore = false
  if (nextPageUrl) {
    hasMore = true
  } else if (hasExplicitLastPage) {
    hasMore = currentPage < lastPage
  } else {
    hasMore = list.length >= pageSize
  }

  return {
    list,
    currentPage,
    lastPage: lastPage ?? currentPage,
    hasMore,
    nextPageUrl,
  }
}

export const getCityHubTabByPath = pathSegment =>
  VISIBLE_CITY_HUB_TABS.find(tab => tab.path === pathSegment) || null

export const getCityHubTabByKey = key =>
  CITY_HUB_TABS.find(
    tab => tab.key === key || (tab.legacyKeys || []).includes(key),
  ) || null

export const cityHubTabPath = (tab, cityId) => {
  const path = typeof tab === "string" ? tab : tab?.path
  if (!path) return "/city-hub"
  if (!cityId) return `/city-hub/${path}`
  return `/city-hub/${cityId}/${path}`
}

export const cityHubShadowEventPath = (cityId, eventId) => {
  const listingPath = cityHubTabPath("shadow-working-events", cityId)
  if (!eventId) return listingPath
  return `${listingPath}/${eventId}`
}

export const cityHubCallsPath = cityId =>
  cityHubTabPath("calls-for-artists", cityId)
